import { PlaybackError } from './PlaybackError';

/**
 * PIN 1 — derive the HLS manifest from a `/video/play` response body.
 *
 * Pure: no fetch, no window, no React. Unit-tested directly.
 *
 * @param {object} body - the parsed `/video/play/:id/:w/:h` response
 * @returns {{url: string, live: boolean, trickplayUrl: string|null}}
 * @throws {PlaybackError} `geo_blocked` or `manifest_unavailable`
 */
export function manifestFromPlayResponse(body) {
  // Checked BEFORE the url check: a geo-blocked body carries an empty
  // `video_m3u8`, and "not available in your region" is the more specific
  // reason. `geoCheck` is inverted and fail-OPEN — only an explicit `false`
  // blocks, so a missing/undefined value stays allowed (the bridge's own
  // behaviour; reading it any other way stops streams that may legally run).
  if (body?.geoCheck === false) {
    throw new PlaybackError('geo_blocked');
  }

  // `video_m3u8` is the only field present on every shape; `live.url` is the
  // same signed manifest on a live video and exists only there.
  const url = body?.video_m3u8 || body?.live?.url;
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
    throw new PlaybackError('manifest_unavailable');
  }

  // The scrub-bar preview (trickplay) sprite index. Same absolute-http(s) gate
  // as the manifest — it is handed straight to a plugin as a URL — but a bad
  // one yields `null` instead of throwing: the field is legitimately absent on
  // live (the captures carry an explicit `null`), and losing the preview must
  // never cost the viewer the stream.
  const trickplay = body.trickplay_vtt_path;
  const trickplayUrl =
    typeof trickplay === 'string' && /^https?:\/\//i.test(trickplay) ? trickplay : null;

  // That exact string. NOT `!== 'VOD'` — the VOD spelling is "Gigcasters API".
  return { url, live: body.source === 'Live', trickplayUrl };
}

export default manifestFromPlayResponse;
